import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  actionsForTarget,
  isPoiSensor,
  minActionCredits,
  normalizeCreditActions,
  refreshCredits,
} from 'src/app/services/operators/voyageradar-credit-actions';
import { VoyageRadarPlaybackController } from '../../voyage-radar-playback.controller';
import { voyageSensorScene } from '../../voyage-credits.model';
import {
  resolveVoyageRefreshState,
  VoyageRefreshState,
} from './voyage-sensor-row.model';

/**
 * Voyage Radar sensor row — the Refresh action.
 *
 * Replaces the shared `mat-checkbox` on the sensor header, for this operator
 * only. That checkbox was not a toggle: ticking it called the operator and spent
 * a refresh credit, unticking it never gave one back, and every failure path set
 * it back to unticked behind the user's back. A button that names its price is
 * the honest control for a billed, one-way action.
 *
 * It is also the only place Voyage Radar's refresh balance appears. Spire and
 * WeatherEx both print "N refreshes left" in their sensor rows; Voyage Radar —
 * the operator whose whole panel is built around consuming refreshes — printed
 * nothing.
 *
 * Purely presentational: the state comes from a pure resolver and the click is
 * emitted upward. The host owns the confirmation dialog and the actual pull,
 * matching the four sections below it.
 *
 * Change detection stays Default to match its siblings; `state$` arrives through
 * the async pipe so a pull in flight repaints the button on its own.
 */
@Component({
  selector: 'app-voyage-refresh-button',
  templateUrl: './voyage-refresh-button.component.html',
  styleUrls: ['./voyage-refresh-button.component.scss'],
})
export class VoyageRefreshButtonComponent {
  /** The Voyage Radar sensor this button refreshes. */
  @Input() sensor: any;
  /** Read for `loading` and `mode` — the same state the map toolbar renders. */
  @Input() controller!: VoyageRadarPlaybackController;

  /**
   * The user asked for a refresh. Emits the resolved state so the host knows
   * whether to confirm first without re-deriving the balance.
   */
  @Output() refresh = new EventEmitter<VoyageRefreshState>();

  /**
   * The scene the pull would use — the same resolution
   * `VoyageMapController.refreshSensor` performs, so the button cannot report a
   * TOI state for one scene while the pull reads another. Shared with the
   * sensor checkbox, which is disabled off the same window.
   */
  get scene(): any {
    return voyageSensorScene(this.sensor);
  }

  state(loading: boolean, playbackRunning: boolean): VoyageRefreshState {
    // Prices come from the sensor's catalogue snapshot (operator doc), with the
    // code-side defaults as fallback — the resolver itself stays pure.
    //
    // `refreshCredits` picks the entry for THIS sensor's target shape, so an
    // area task is priced by /v1/vessels/bbox and a point task by
    // /v1/vessels/nearby — the same choice the server makes from the same
    // footprint. The stranded-wallet floor is taken over the actions this task
    // can actually reach, so the area refresh cannot make a point task's wallet
    // look spendable when nothing here can spend it. That exclusion runs one
    // way only now: `nearby` carries no target because it prices an endpoint
    // due to serve a second caller — the per-vessel search of plan steps 4-8,
    // not built yet — so an area task is not filtered out of that row and this
    // floor counts it rather than dropping it. Until that caller lands, a point
    // refresh is still the only gesture that reaches the endpoint.
    const actions = actionsForTarget(
      normalizeCreditActions(this.sensor?.apiCreditActions),
      isPoiSensor(this.sensor),
    );
    return resolveVoyageRefreshState({
      sensor: this.sensor,
      scene: this.scene,
      loading,
      playbackRunning,
      cost: refreshCredits(this.sensor),
      minActionCost: minActionCredits(actions),
    });
  }

  /**
   * The header this sits in toggles the expansion panel on click, so the event
   * must not reach it — refreshing and collapsing the panel are unrelated.
   */
  onClick(event: Event, state: VoyageRefreshState): void {
    event.stopPropagation();
    if (state.disabled) return;
    this.refresh.emit(state);
  }
}
